import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { reportsAPI } from "../../utils/reportsAPI";
import { analysisAPI } from "../../utils/analysisAPI";
import { organizationsAPI } from "../../utils/organizationsAPI";
import { datasetsAPI } from "../../utils/datasetsAPI";
import { useAuth } from "../../context/AuthContext";
import { showToast } from "../../components/Toast";
import { showConfirm } from "../../components/ConfirmModal";
import Pagination from "../../components/Pagination";
import { EmptyState, ErrorState } from "../../components/EmptyStates";

const sectionsToContent = (sections) =>
  sections
    .filter((s) => s.title || s.content)
    .map((s) => {
      const titleLine = s.title ? `## ${s.title}` : "";
      if (!titleLine) return s.content || "";
      return `${titleLine}\n\n${s.content || ""}`;
    })
    .join("\n\n");

function DesignReport() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const orgIdFromUrl = searchParams.get("org");
  const analysisIdFromUrl = searchParams.get("analysisId");
  const [report, setReport] = useState({
    title: "",
    description: "",
    content: "",
    subject: "",
  });
  const [templates, setTemplates] = useState([]);
  const [analyses, setAnalyses] = useState([]);
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [sections, setSections] = useState([
    { id: 1, title: "Introduction", content: "" },
  ]);
  const [activeSectionId, setActiveSectionId] = useState(1);
  const [accessibleOrgs, setAccessibleOrgs] = useState([]);
  const [chartBlocks, setChartBlocks] = useState([]);
  const [activeChartId, setActiveChartId] = useState(null);
  const [datasets, setDatasets] = useState([]);
  const [schemaCache, setSchemaCache] = useState({}); // { [datasetId]: { columns: [...] } }
  const [chartDataLoading, setChartDataLoading] = useState(false);

  const resetToNewReport = () => {
    setSelectedTemplate(null);
    setSelectedAnalysis(null);
    setReport({
      title: "",
      description: "",
      content: "",
      subject: "",
    });
    setSections([{ id: 1, title: "Introduction", content: "" }]);
    setActiveSectionId(1);
    setChartBlocks([]);
    setActiveChartId(null);
  };

  const effectiveOrgId = useMemo(() => {
    if (orgIdFromUrl) return Number(orgIdFromUrl);
    if (user?.organization && typeof user.organization === "object") {
      return user.organization.id;
    }
    if (user?.organization) return user.organization;
    if (accessibleOrgs.length > 0) return accessibleOrgs[0].id;
    return null;
  }, [orgIdFromUrl, user, accessibleOrgs]);

  // Load accessible organizations so analysts without a primary org can still work
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await organizationsAPI.getAccessible();
        if (cancelled) return;
        const data = response?.data || response;
        const list = Array.isArray(data) ? data : data?.results || [];
        setAccessibleOrgs(list);
      } catch {
        if (!cancelled) setAccessibleOrgs([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    loadTemplates(currentPage);
  }, [effectiveOrgId, currentPage]);

  // Load datasets for the current organization (for dataset-driven charts)
  useEffect(() => {
    if (!effectiveOrgId) {
      setDatasets([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await datasetsAPI.list(effectiveOrgId, {
          page_size: 200,
          has_columns: true,
        });
        if (cancelled) return;
        const data = response?.data || response;
        const list = Array.isArray(data) ? data : data?.results || [];
        setDatasets(list);
      } catch {
        if (!cancelled) setDatasets([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [effectiveOrgId]);

  // Helper: load schema for a dataset (cached)
  const loadSchema = async (datasetId) => {
    if (!effectiveOrgId || !datasetId) return null;
    if (schemaCache[datasetId]) return schemaCache[datasetId];
    try {
      const response = await datasetsAPI.getSchema(effectiveOrgId, datasetId);
      const data = response?.data || response;
      const schema = data?.columns || data?.data?.columns || [];
      setSchemaCache((prev) => ({ ...prev, [datasetId]: schema }));
      return schema;
    } catch {
      return null;
    }
  };

  // Load all completed analyses the analyst can access (across organizations)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const out = await analysisAPI.listAnalyses(null, {
          status: "COMPLETED",
        });
        const rawList = Array.isArray(out) ? out : out?.list || [];
        // Deduplicate by id in case backend or multiple org calls return duplicates
        const seen = new Set();
        const unique = [];
        for (const a of rawList) {
          if (!a || !a.id || seen.has(a.id)) continue;
          seen.add(a.id);
          unique.push(a);
        }
        if (!cancelled) setAnalyses(unique);
      } catch {
        if (!cancelled) setAnalyses([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Keep flat content in sync with structured sections for backend compatibility
  useEffect(() => {
    setReport((prev) => ({
      ...prev,
      content: sectionsToContent(sections),
    }));
  }, [sections]);

  useEffect(() => {
    if (!analysisIdFromUrl) return;
    const id = parseInt(analysisIdFromUrl, 10);
    const a = analyses.find((x) => x.id === id);
    if (a) {
      setSelectedAnalysis(a);
      const r = a.results || {};
      const summary = r.summary || "";
      const insights = Array.isArray(r.insights)
        ? r.insights.join("\n")
        : r.insights || "";
      const recs = Array.isArray(r.recommendations)
        ? r.recommendations.join("\n")
        : r.recommendations || "";
      const clientName = a.client_name || (a.client && a.client.name) || "";
      setReport((prev) => ({
        ...prev,
        title:
          prev.title ||
          `${a.model_name || "Analysis"} Report${clientName ? ` - ${clientName}` : ""}`,
        description: prev.description || summary.slice(0, 200),
        subject: prev.subject || (clientName ? `Report for ${clientName}` : ""),
        content: [summary, "Insights:", insights, "Recommendations:", recs]
          .filter(Boolean)
          .join("\n\n"),
      }));
      return;
    }
    analysisAPI
      .getAnalysis(id)
      .then((fetched) => {
        setSelectedAnalysis(fetched);
        setAnalyses((prev) =>
          prev.some((x) => x.id === fetched.id) ? prev : [fetched, ...prev],
        );
        const r = fetched.results || {};
        const summary = r.summary || "";
        const insights = Array.isArray(r.insights)
          ? r.insights.join("\n")
          : r.insights || "";
        const recs = Array.isArray(r.recommendations)
          ? r.recommendations.join("\n")
          : r.recommendations || "";
        const clientName =
          fetched.client_name || (fetched.client && fetched.client.name) || "";
        const combinedContent = [
          summary,
          "Insights:",
          insights,
          "Recommendations:",
          recs,
        ]
          .filter(Boolean)
          .join("\n\n");
        setReport((prev) => ({
          ...prev,
          title:
            prev.title ||
            `${fetched.model_name || "Analysis"} Report${
              clientName ? ` - ${clientName}` : ""
            }`,
          description: prev.description || summary.slice(0, 200),
          subject:
            prev.subject || (clientName ? `Report for ${clientName}` : ""),
          content: combinedContent,
        }));
        setSections([
          {
            id: 1,
            title: fetched.model_name || "Analysis Summary",
            content: combinedContent,
          },
        ]);
        setActiveSectionId(1);
        setChartBlocks([]);
        setActiveChartId(null);
      })
      .catch(() => {});
  }, [analysisIdFromUrl, analyses]);

  const loadTemplates = async (page = 1) => {
    if (!effectiveOrgId) {
      // No organization yet – allow independent report creation without blocking the UI.
      setTemplates([]);
      setPagination(null);
      setError(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await reportsAPI.list(effectiveOrgId, {
        page,
        page_size: 10,
      });
      const data = response?.data || response;
      const reportsList = Array.isArray(data) ? data : data?.results || [];
      setTemplates(reportsList);

      if (response?.pagination) {
        setPagination(response.pagination);
      }
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSelectAnalysis = (analysis) => {
    setSelectedAnalysis(analysis || null);
    if (!analysis) return;
    const r = analysis.results || {};
    const summary = r.summary || "";
    const insights = Array.isArray(r.insights)
      ? r.insights.join("\n")
      : r.insights || "";
    const recs = Array.isArray(r.recommendations)
      ? r.recommendations.join("\n")
      : r.recommendations || "";
    const clientName =
      analysis.client_name || (analysis.client && analysis.client.name) || "";
    const combinedContent = [
      summary,
      "Insights:",
      insights,
      "Recommendations:",
      recs,
    ]
      .filter(Boolean)
      .join("\n\n");
    setReport((prev) => ({
      ...prev,
      title:
        prev.title ||
        `${analysis.model_name || "Analysis"} Report${
          clientName ? ` - ${clientName}` : ""
        }`,
      description: prev.description || summary.slice(0, 200),
      subject: prev.subject || (clientName ? `Report for ${clientName}` : ""),
      content: combinedContent,
    }));
    setSections([
      {
        id: 1,
        title: analysis.model_name || "Analysis Summary",
        content: combinedContent,
      },
    ]);
    setActiveSectionId(1);
    setChartBlocks([]);
    setActiveChartId(null);

    // If this analysis clearly belongs to a specific org, sync the report org selector to it
    const orgForAnalysis =
      analysis.client_id ||
      analysis.organization_id ||
      (analysis.client && analysis.client.id);
    if (orgForAnalysis && orgForAnalysis !== effectiveOrgId) {
      const params = new URLSearchParams(location.search);
      params.set("org", orgForAnalysis);
      navigate(`${location.pathname}?${params.toString()}`);
    }
  };

  const handleSubmit = async (
    targetStatus = "DRAFT",
    markSubmitted = false,
  ) => {
    if (!effectiveOrgId) {
      showToast.warning(
        "You need a client organization to save this report. Ask an admin to assign you to a client, or open Design Report from an Assigned Client.",
      );
      return;
    }

    try {
      setSubmitting(true);
      const baseSections = sections.length
        ? sections
        : [
            {
              id: 1,
              title: report.title || "Report Body",
              content: report.content || "",
            },
          ];

      const hasCharts = chartBlocks.length > 0;

      const payload = {
        title: report.title,
        subject: report.subject || report.title,
        report_type: "custom",
        status: targetStatus,
        submitted: markSubmitted,
      };

      if (hasCharts) {
        const structured = {
          version: 1,
          sections: baseSections,
          charts: chartBlocks,
          plainText: report.content || sectionsToContent(baseSections),
        };
        payload.content_type = "json";
        payload.content = JSON.stringify(structured);
      } else {
        payload.content_type = "text";
        payload.content = report.content || sectionsToContent(baseSections);
      }
      if (report.description)
        payload.subject = payload.subject || report.description;
      // We keep the content prefilled from the analysis, but do not hard-link the
      // report to the analysis record, so that stale or deleted analyses do not
      // cause database constraint errors when saving.
      const isEditing = !!selectedTemplate?.id;
      if (isEditing) {
        const updated = await reportsAPI.update(
          effectiveOrgId,
          selectedTemplate.id,
          payload,
        );
        showToast.success("Report updated successfully!");
        const updatedData = updated?.data || updated;
        setSelectedTemplate((prev) =>
          prev ? { ...prev, ...updatedData } : updatedData,
        );
        // Refresh list so all portals see latest version
        loadTemplates(currentPage);
      } else {
        const created = await reportsAPI.create(effectiveOrgId, payload);
        showToast.success("Report created successfully!");
        const createdData = created?.data || created;
        // Switch into "editing" mode for the newly created report so subsequent saves update it.
        setSelectedTemplate(createdData || null);
        setReport((prev) => ({
          ...prev,
          title: createdData?.title ?? prev.title,
          subject: createdData?.subject ?? prev.subject,
          content: createdData?.content ?? prev.content,
          description: createdData?.description ?? prev.description,
        }));
        if (createdData?.content) {
          setSections([
            {
              id: 1,
              title: createdData?.title || "Report Body",
              content: createdData.content,
            },
          ]);
          setActiveSectionId(1);
        }
        loadTemplates(1);
      }
    } catch (err) {
      showToast.error("Failed to save report: " + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!effectiveOrgId || !selectedTemplate) return;
    const confirmed = await showConfirm({
      title: "Delete report?",
      message:
        "This action cannot be undone. The report will be removed from this organization.",
      confirmText: "Delete",
      cancelText: "Cancel",
      type: "danger",
    });
    if (!confirmed) return;
    try {
      setSubmitting(true);
      await reportsAPI.delete(effectiveOrgId, selectedTemplate.id);
      showToast.success("Report deleted successfully!");
      setSelectedTemplate(null);
      setSelectedAnalysis(null);
      setReport({ title: "", description: "", content: "", subject: "" });
      loadTemplates(1);
    } catch (err) {
      showToast.error("Failed to delete report: " + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleOrgChange = (e) => {
    const value = e.target.value;
    const params = new URLSearchParams(location.search);
    if (value) {
      params.set("org", value);
    } else {
      params.delete("org");
    }
    navigate(`${location.pathname}?${params.toString()}`);
  };

  return (
    <div className="page-content">
      <div className="card">
        <h2 className="card-title">Design Report</h2>
        {!effectiveOrgId && (
          <div style={{ marginTop: "12px" }}>
            <EmptyState
              icon="📝"
              title="No organization selected"
              message="You can still design an independent report. Your work will be saved to an organization once one is available."
              action={
                <button
                  className="btn btn-primary"
                  type="button"
                  onClick={() => {
                    setReport({
                      title: "",
                      description: "",
                      content: "",
                      subject: "",
                    });
                    setSections([
                      { id: 1, title: "Introduction", content: "" },
                    ]);
                    setActiveSectionId(1);
                  }}
                >
                  Create Report
                </button>
              }
            />
          </div>
        )}

        {error && (
          <div style={{ marginTop: "16px" }}>
            <ErrorState
              error={error}
              onRetry={() => loadTemplates(currentPage)}
            />
          </div>
        )}

        {/* Organization selector */}
        {accessibleOrgs.length > 0 && (
          <div
            className="form-group"
            style={{ marginTop: "16px", maxWidth: "360px" }}
          >
            <label>Report Organization</label>
            <select
              value={effectiveOrgId || ""}
              onChange={handleOrgChange}
              style={{
                width: "100%",
                padding: "8px 12px",
                borderRadius: "6px",
                border: "1px solid var(--border-grey, #ddd)",
              }}
            >
              <option value="">Select organization...</option>
              {accessibleOrgs.map((org) => (
                <option key={org.id} value={org.id}>
                  {org.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Top action bar */}
        <div
          style={{
            marginTop: "20px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "16px",
            flexWrap: "wrap",
          }}
        >
          <div>
            <p
              style={{
                margin: 0,
                fontSize: "13px",
                color: "var(--text-grey)",
              }}
            >
              Build a polished, client-ready report using sections, rich text,
              and optional analysis summaries.
            </p>
          </div>
          <div
            style={{
              display: "flex",
              gap: "8px",
              flexWrap: "wrap",
            }}
          >
            <button
              type="button"
              className="btn btn-secondary"
              disabled={submitting}
              onClick={() => handleSubmit("DRAFT", false)}
            >
              Save Draft
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={submitting || !effectiveOrgId}
              onClick={() => handleSubmit("PUBLISHED", true)}
            >
              Publish
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                const previewWindow = window.open("", "_blank");
                if (!previewWindow) return;
                previewWindow.document.write(
                  `<html><head><title>${report.title || "Report Preview"}</title></head><body><pre style="font-family: system-ui, -apple-system, Segoe UI, sans-serif; white-space: pre-wrap;">${report.content}</pre></body></html>`,
                );
              }}
            >
              Preview
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => window.print()}
            >
              Export PDF
            </button>
          </div>
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(260px, 0.9fr) 2.1fr",
            gap: "24px",
            marginTop: "20px",
          }}
        >
          {/* Left sidebar: Templates & Sections */}
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "12px",
                marginBottom: "12px",
              }}
            >
              <h3 style={{ margin: 0 }}>Templates</h3>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => {
                  // Clear any template selection so saves create a new report.
                  resetToNewReport();
                  const params = new URLSearchParams(location.search);
                  params.delete("analysisId");
                  navigate(`${location.pathname}?${params.toString()}`);
                }}
              >
                + New report
              </button>
            </div>
            <div
              style={{ display: "flex", flexDirection: "column", gap: "12px" }}
            >
              {templates.map((item) => (
                <div
                  key={item.id}
                  className="card"
                  style={{
                    padding: "12px",
                    cursor: "pointer",
                    outline:
                      selectedTemplate?.id === item.id
                        ? "2px solid rgba(99, 102, 241, 0.35)"
                        : "none",
                  }}
                  onClick={() => {
                    setSelectedTemplate(item);
                    // Load legacy text reports and new JSON-structured reports
                    if (item.content_type === "json" && item.content) {
                      try {
                        const parsed = JSON.parse(item.content);
                        const parsedSections = Array.isArray(parsed.sections)
                          ? parsed.sections
                          : [
                              {
                                id: 1,
                                title: item.title || "Report Body",
                                content: parsed.plainText || "",
                              },
                            ];
                        const parsedCharts = Array.isArray(parsed.charts)
                          ? parsed.charts
                          : [];
                        setReport({
                          title: item.title || "",
                          description: item.description || "",
                          subject: item.subject || "",
                          content:
                            parsed.plainText ||
                            sectionsToContent(parsedSections) ||
                            "",
                        });
                        setSections(parsedSections);
                        setChartBlocks(parsedCharts);
                        setActiveSectionId(parsedSections[0]?.id || 1);
                        setActiveChartId(parsedCharts[0]?.id || null);
                      } catch {
                        setReport({
                          title: item.title || "",
                          description: item.description || "",
                          subject: item.subject || "",
                          content: item.content || "",
                        });
                        setSections([
                          {
                            id: 1,
                            title: item.title || "Report Body",
                            content: item.content || "",
                          },
                        ]);
                        setChartBlocks([]);
                        setActiveSectionId(1);
                        setActiveChartId(null);
                      }
                    } else {
                      setReport({
                        title: item.title || "",
                        description: item.description || "",
                        subject: item.subject || "",
                        content: item.content || "",
                      });
                      setSections([
                        {
                          id: 1,
                          title: item.title || "Report Body",
                          content: item.content || "",
                        },
                      ]);
                      setChartBlocks([]);
                      setActiveSectionId(1);
                      setActiveChartId(null);
                    }
                  }}
                >
                  <div style={{ fontWeight: 600 }}>
                    {item.title || item.name}
                  </div>
                  <div style={{ color: "var(--text-grey)", fontSize: "12px" }}>
                    {new Date(item.created_at).toLocaleDateString()}
                  </div>
                </div>
              ))}
            </div>
            {pagination && (
              <Pagination
                pagination={pagination}
                onPageChange={handlePageChange}
              />
            )}

            {/* Sections list */}
            <div style={{ marginTop: "24px" }}>
              <h3 style={{ marginBottom: "8px" }}>Sections</h3>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "6px",
                }}
              >
                {sections.map((section) => (
                  <button
                    key={section.id}
                    type="button"
                    className={`btn btn-secondary btn-sm ${
                      activeSectionId === section.id ? "active" : ""
                    }`}
                    style={{
                      justifyContent: "flex-start",
                      textAlign: "left",
                    }}
                    onClick={() => setActiveSectionId(section.id)}
                  >
                    {section.title || "Untitled section"}
                  </button>
                ))}
              </div>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                style={{ marginTop: "10px", width: "100%" }}
                onClick={() => {
                  const nextId =
                    sections.reduce((max, s) => Math.max(max, s.id), 0) + 1;
                  const newSection = {
                    id: nextId,
                    title: `Section ${sections.length + 1}`,
                    content: "",
                  };
                  setSections((prev) => [...prev, newSection]);
                  setActiveSectionId(nextId);
                }}
              >
                + Add Section
              </button>
            </div>
          </div>

          {/* Main editor */}
          <div>
            <div
              style={{ display: "flex", flexDirection: "column", gap: "16px" }}
            >
              <div className="form-group">
                <label>Choose which analysis to base this report on</label>
                <select
                  value={selectedAnalysis?.id ?? ""}
                  onChange={(e) => {
                    const id = e.target.value ? Number(e.target.value) : null;
                    const a = id ? analyses.find((x) => x.id === id) : null;
                    handleSelectAnalysis(a);
                  }}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    border: "1px solid var(--border-grey, #ddd)",
                    borderRadius: "6px",
                  }}
                >
                  <option value="">— Create from scratch —</option>
                  {analyses.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.model_name} — {a.data_table || a.dataset || "dataset"}{" "}
                      {a.client_name ? `(${a.client_name})` : ""}
                    </option>
                  ))}
                </select>
                {selectedAnalysis && (
                  <p
                    style={{
                      marginTop: "6px",
                      fontSize: "13px",
                      color: "var(--text-grey)",
                    }}
                  >
                    Summary and insights from this analysis have been filled
                    below. Edit as needed.
                  </p>
                )}
              </div>
              <div className="form-group">
                <label>Report Title</label>
                <input
                  type="text"
                  value={report.title}
                  onChange={(e) =>
                    setReport({ ...report, title: e.target.value })
                  }
                  placeholder="Q1 2026 Sales Analysis"
                />
              </div>
              <div className="form-group">
                <label>Subject / short description</label>
                <input
                  type="text"
                  value={report.subject}
                  onChange={(e) =>
                    setReport({ ...report, subject: e.target.value })
                  }
                  placeholder="e.g. Q1 Inventory, Client Summary"
                />
              </div>
              <div className="form-group">
                <label>Section Content</label>
                {(() => {
                  const activeSection =
                    sections.find((s) => s.id === activeSectionId) ||
                    sections[0];
                  return (
                    <>
                      <input
                        type="text"
                        value={activeSection?.title || ""}
                        onChange={(e) => {
                          const title = e.target.value;
                          setSections((prev) =>
                            prev.map((s) =>
                              s.id === activeSection.id ? { ...s, title } : s,
                            ),
                          );
                        }}
                        placeholder="Section title (e.g. Executive Summary)"
                        style={{ marginBottom: "8px" }}
                      />
                      <textarea
                        value={activeSection?.content || ""}
                        onChange={(e) => {
                          const content = e.target.value;
                          setSections((prev) =>
                            prev.map((s) =>
                              s.id === activeSection.id ? { ...s, content } : s,
                            ),
                          );
                          // `report.content` is kept in sync via sectionsToContent useEffect
                        }}
                        rows="10"
                        placeholder="Write this section of your report here..."
                        style={{
                          resize: "vertical",
                          fontFamily: "inherit",
                          padding: "12px",
                          border: "1px solid var(--border-grey)",
                          borderRadius: "6px",
                        }}
                      />
                    </>
                  );
                })()}
              </div>
              {/* Charts configuration */}
              <div className="form-group">
                <label>Charts in this report (optional)</label>
                <p
                  style={{
                    marginTop: "4px",
                    fontSize: "13px",
                    color: "var(--text-grey)",
                  }}
                >
                  Create any chart you need: choose type and enter your own data
                  points. These charts will be shown to admins and clients
                  inside the report.
                </p>

                {/* Chart list */}
                {chartBlocks.length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "6px",
                      marginTop: "8px",
                    }}
                  >
                    {chartBlocks.map((chart) => (
                      <button
                        key={chart.id}
                        type="button"
                        className={`btn btn-secondary btn-sm ${
                          activeChartId === chart.id ? "active" : ""
                        }`}
                        style={{
                          minWidth: "140px",
                          justifyContent: "flex-start",
                        }}
                        onClick={() => setActiveChartId(chart.id)}
                      >
                        {chart.title || "Untitled chart"}
                      </button>
                    ))}
                  </div>
                )}

                {/* Active chart editor */}
                {chartBlocks.length > 0 && (
                  <div
                    className="card"
                    style={{ marginTop: "10px", padding: "12px 14px" }}
                  >
                    {(() => {
                      const chart =
                        chartBlocks.find((c) => c.id === activeChartId) ||
                        chartBlocks[0];
                      if (!chart) return null;

                      const updateChart = (patch) => {
                        setChartBlocks((prev) =>
                          prev.map((c) =>
                            c.id === chart.id ? { ...c, ...patch } : c,
                          ),
                        );
                      };

                      const isDatasetSource = chart.source === "dataset";
                      const chartSchema = chart.dataset_id
                        ? schemaCache[chart.dataset_id] || []
                        : [];
                      // Label dropdown: show all columns
                      const labelCols = chartSchema;
                      // Value dropdown: only columns that are numeric or
                      // whose actual row data contains numeric values
                      const valueCols = chartSchema.filter(
                        (col) =>
                          col.column_type === "number" ||
                          col.has_numeric_data === true,
                      );

                      const handleDatasetChange = async (datasetId) => {
                        updateChart({
                          dataset_id: datasetId,
                          label_field: "",
                          value_field: "",
                          dataPoints: [],
                        });
                        if (datasetId) await loadSchema(datasetId);
                      };

                      const handleGenerateChart = async () => {
                        if (
                          !chart.dataset_id ||
                          !chart.label_field ||
                          !chart.value_field
                        ) {
                          showToast.warning(
                            "Please select a dataset, label field, and value field first.",
                          );
                          return;
                        }
                        try {
                          setChartDataLoading(true);
                          const response = await datasetsAPI.generateChartData({
                            dataset_id: chart.dataset_id,
                            organization_id: effectiveOrgId,
                            label_field: chart.label_field,
                            value_field: chart.value_field,
                            aggregation: chart.aggregation || "sum",
                          });
                          const result = response?.data || response;
                          const labels =
                            result?.labels || result?.data?.labels || [];
                          const values =
                            result?.values || result?.data?.values || [];
                          const dataPoints = labels.map((label, i) => ({
                            label,
                            value: values[i] ?? 0,
                          }));
                          updateChart({ dataPoints });
                          showToast.success(
                            `Generated ${dataPoints.length} data points from dataset`,
                          );
                        } catch (err) {
                          showToast.error(
                            "Failed to generate chart data: " + err.message,
                          );
                        } finally {
                          setChartDataLoading(false);
                        }
                      };

                      return (
                        <>
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns:
                                "minmax(0, 2fr) minmax(0, 1.1fr)",
                              gap: "12px",
                            }}
                          >
                            <div>
                              <label
                                style={{
                                  display: "block",
                                  fontSize: "13px",
                                  marginBottom: "4px",
                                }}
                              >
                                Chart title
                              </label>
                              <input
                                type="text"
                                value={chart.title || ""}
                                onChange={(e) =>
                                  updateChart({ title: e.target.value })
                                }
                                placeholder="e.g. Inventory by status"
                              />
                            </div>
                            <div>
                              <label
                                style={{
                                  display: "block",
                                  fontSize: "13px",
                                  marginBottom: "4px",
                                }}
                              >
                                Chart type
                              </label>
                              <select
                                value={chart.type || "bar"}
                                onChange={(e) =>
                                  updateChart({ type: e.target.value })
                                }
                                style={{ width: "100%", padding: "8px 10px" }}
                              >
                                <option value="bar">Bar</option>
                                <option value="line">Line</option>
                                <option value="pie">Donut</option>
                              </select>
                            </div>
                          </div>

                          <div style={{ marginTop: "8px" }}>
                            <label
                              style={{
                                display: "block",
                                fontSize: "13px",
                                marginBottom: "4px",
                              }}
                            >
                              Description (optional)
                            </label>
                            <textarea
                              rows="2"
                              value={chart.description || ""}
                              onChange={(e) =>
                                updateChart({ description: e.target.value })
                              }
                              placeholder="Explain what this chart is showing..."
                              style={{
                                resize: "vertical",
                                fontFamily: "inherit",
                                padding: "8px 10px",
                                borderRadius: "6px",
                                border: "1px solid var(--border-grey)",
                              }}
                            />
                          </div>

                          {/* Data source toggle */}
                          <div style={{ marginTop: "10px" }}>
                            <label
                              style={{
                                display: "block",
                                fontSize: "13px",
                                marginBottom: "4px",
                              }}
                            >
                              Data source
                            </label>
                            <div style={{ display: "flex", gap: "8px" }}>
                              <button
                                type="button"
                                className={`btn btn-sm ${!isDatasetSource ? "btn-primary" : "btn-secondary"}`}
                                onClick={() =>
                                  updateChart({ source: "manual" })
                                }
                              >
                                Manual
                              </button>
                              <button
                                type="button"
                                className={`btn btn-sm ${isDatasetSource ? "btn-primary" : "btn-secondary"}`}
                                onClick={() =>
                                  updateChart({ source: "dataset" })
                                }
                                disabled={datasets.length === 0}
                                title={
                                  datasets.length === 0
                                    ? "No datasets available for this organization"
                                    : ""
                                }
                              >
                                From Dataset
                              </button>
                            </div>
                          </div>

                          {/* Dataset-driven configuration */}
                          {isDatasetSource && (
                            <div
                              style={{
                                marginTop: "10px",
                                display: "flex",
                                flexDirection: "column",
                                gap: "8px",
                              }}
                            >
                              <div
                                style={{
                                  display: "grid",
                                  gridTemplateColumns: "1fr 1fr",
                                  gap: "10px",
                                }}
                              >
                                <div>
                                  <label
                                    style={{
                                      display: "block",
                                      fontSize: "13px",
                                      marginBottom: "4px",
                                    }}
                                  >
                                    Dataset
                                  </label>
                                  <select
                                    value={chart.dataset_id || ""}
                                    onChange={(e) =>
                                      handleDatasetChange(
                                        e.target.value
                                          ? Number(e.target.value)
                                          : "",
                                      )
                                    }
                                    style={{
                                      width: "100%",
                                      padding: "8px 10px",
                                    }}
                                  >
                                    <option value="">Select dataset...</option>
                                    {datasets.map((ds) => (
                                      <option key={ds.id} value={ds.id}>
                                        {ds.label || ds.name}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                                <div>
                                  <label
                                    style={{
                                      display: "block",
                                      fontSize: "13px",
                                      marginBottom: "4px",
                                    }}
                                  >
                                    Aggregation
                                  </label>
                                  <select
                                    value={chart.aggregation || "sum"}
                                    onChange={(e) =>
                                      updateChart({
                                        aggregation: e.target.value,
                                      })
                                    }
                                    style={{
                                      width: "100%",
                                      padding: "8px 10px",
                                    }}
                                  >
                                    <option value="sum">Sum</option>
                                    <option value="count">Count</option>
                                    <option value="avg">Average</option>
                                  </select>
                                </div>
                              </div>
                              {chart.dataset_id && chartSchema.length > 0 && (
                                <div
                                  style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 1fr",
                                    gap: "10px",
                                  }}
                                >
                                  <div>
                                    <label
                                      style={{
                                        display: "block",
                                        fontSize: "13px",
                                        marginBottom: "4px",
                                      }}
                                    >
                                      Label field (categories)
                                    </label>
                                    <select
                                      value={chart.label_field || ""}
                                      onChange={(e) =>
                                        updateChart({
                                          label_field: e.target.value,
                                        })
                                      }
                                      style={{
                                        width: "100%",
                                        padding: "8px 10px",
                                      }}
                                    >
                                      <option value="">Select column...</option>
                                      {labelCols.map((col) => (
                                        <option key={col.key} value={col.key}>
                                          {col.label} ({col.column_type})
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                  <div>
                                    <label
                                      style={{
                                        display: "block",
                                        fontSize: "13px",
                                        marginBottom: "4px",
                                      }}
                                    >
                                      Value field (numbers)
                                    </label>
                                    <select
                                      value={chart.value_field || ""}
                                      onChange={(e) =>
                                        updateChart({
                                          value_field: e.target.value,
                                        })
                                      }
                                      style={{
                                        width: "100%",
                                        padding: "8px 10px",
                                      }}
                                    >
                                      <option value="">Select column...</option>
                                      {valueCols.map((col) => (
                                        <option key={col.key} value={col.key}>
                                          {col.label} ({col.column_type})
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                </div>
                              )}
                              {chart.dataset_id && chartSchema.length === 0 && (
                                <p
                                  style={{
                                    fontSize: "13px",
                                    color: "var(--text-grey)",
                                    margin: 0,
                                  }}
                                >
                                  Loading schema...
                                </p>
                              )}
                              <div>
                                <button
                                  type="button"
                                  className="btn btn-primary btn-sm"
                                  disabled={
                                    chartDataLoading ||
                                    !chart.dataset_id ||
                                    !chart.label_field ||
                                    !chart.value_field
                                  }
                                  onClick={handleGenerateChart}
                                >
                                  {chartDataLoading
                                    ? "Generating..."
                                    : "Generate Chart Data"}
                                </button>
                              </div>
                            </div>
                          )}

                          {/* Manual data points (shown when source is manual, or as read-only preview for generated data) */}
                          <div style={{ marginTop: "10px" }}>
                            <label
                              style={{
                                display: "block",
                                fontSize: "13px",
                                marginBottom: "4px",
                              }}
                            >
                              Data points{" "}
                              {isDatasetSource && chart.dataPoints?.length > 0
                                ? "(generated)"
                                : ""}
                            </label>
                            <div
                              style={{
                                display: "grid",
                                gridTemplateColumns: "2fr 1fr auto",
                                gap: "6px",
                                alignItems: "center",
                              }}
                            >
                              {(chart.dataPoints || []).map((pt, idx) => (
                                <React.Fragment key={idx}>
                                  <input
                                    type="text"
                                    value={pt.label || ""}
                                    onChange={(e) => {
                                      const label = e.target.value;
                                      updateChart({
                                        dataPoints: (
                                          chart.dataPoints || []
                                        ).map((p, i) =>
                                          i === idx ? { ...p, label } : p,
                                        ),
                                      });
                                    }}
                                    placeholder="Label (e.g. Finished)"
                                    readOnly={isDatasetSource}
                                  />
                                  <input
                                    type="number"
                                    value={pt.value ?? ""}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      updateChart({
                                        dataPoints: (
                                          chart.dataPoints || []
                                        ).map((p, i) =>
                                          i === idx ? { ...p, value } : p,
                                        ),
                                      });
                                    }}
                                    placeholder="Value"
                                    readOnly={isDatasetSource}
                                  />
                                  {!isDatasetSource && (
                                    <button
                                      type="button"
                                      className="btn btn-secondary btn-sm"
                                      onClick={() =>
                                        updateChart({
                                          dataPoints: (
                                            chart.dataPoints || []
                                          ).filter((_p, i) => i !== idx),
                                        })
                                      }
                                    >
                                      Remove
                                    </button>
                                  )}
                                </React.Fragment>
                              ))}
                            </div>
                            {!isDatasetSource && (
                              <button
                                type="button"
                                className="btn btn-secondary btn-sm"
                                style={{ marginTop: "8px" }}
                                onClick={() =>
                                  updateChart({
                                    dataPoints: [
                                      ...(chart.dataPoints || []),
                                      { label: "", value: "" },
                                    ],
                                  })
                                }
                              >
                                + Add data point
                              </button>
                            )}
                          </div>

                          <div style={{ marginTop: "10px" }}>
                            <button
                              type="button"
                              className="btn btn-secondary btn-sm"
                              onClick={() => {
                                setChartBlocks((prev) =>
                                  prev.filter((c) => c.id !== chart.id),
                                );
                                setActiveChartId((prevId) =>
                                  prevId === chart.id ? null : prevId,
                                );
                              }}
                            >
                              Delete chart
                            </button>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                )}

                {/* Add chart button */}
                <div style={{ marginTop: "8px" }}>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => {
                      const newChart = {
                        id: `chart-${Date.now()}`,
                        title: "New chart",
                        description: "",
                        type: "bar",
                        source: "manual",
                        dataPoints: [
                          { label: "Category A", value: 10 },
                          { label: "Category B", value: 20 },
                        ],
                      };
                      setChartBlocks((prev) => [...prev, newChart]);
                      setActiveChartId(newChart.id);
                    }}
                  >
                    + Add chart
                  </button>
                </div>
              </div>
              <div style={{ display: "flex", gap: "8px" }}>
                <button
                  className="btn btn-primary"
                  onClick={() => handleSubmit()}
                  disabled={submitting}
                >
                  {submitting
                    ? selectedTemplate
                      ? "Saving..."
                      : "Creating..."
                    : selectedTemplate
                      ? "Save Changes"
                      : "Submit Report"}
                </button>
                {selectedTemplate && (
                  <button
                    className="btn btn-danger"
                    onClick={handleDelete}
                    disabled={submitting}
                  >
                    Delete Report
                  </button>
                )}
              </div>
            </div>

            {selectedTemplate && (
              <div style={{ marginTop: "24px" }}>
                <h3>Template Preview</h3>
                <p>
                  {selectedTemplate.description ||
                    selectedTemplate.summary ||
                    "N/A"}
                </p>

                {selectedTemplate.content && (
                  <>
                    <h4 style={{ marginTop: "16px" }}>Content</h4>
                    <p>{selectedTemplate.content}</p>
                  </>
                )}

                <div
                  className="chart-placeholder"
                  style={{ marginTop: "12px" }}
                >
                  Template preview
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default DesignReport;
